import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppSelector } from '../../app/hook';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { BotCode } from '../bot/types';
import { selectAppearanceState } from './appearanceSlice';
import CallBot from './preview/CallBot';
import TextWidget from './preview/TextWidget';
import VideoToggleIcon from './preview/VideoToggleIcon';
import VideoWidget from './preview/VideoWidget';
import WidgetToggleIcon from './preview/WidgetToggleIcon';
import { IAiAppearance } from './types';

const AppearancePreview = () => {
  const { configs, bot_code } = useAppSelector(selectAppearanceState);

  const sampleTextWidgetMessages: Array<{
    userType: 'bot' | 'user';
    data: { type: string; value: string };
  }> = [
    {
      userType: 'bot',
      data: {
        type: 'text',
        value: 'This is a message from Louise, it would appear like this to your users',
      },
    },
    {
      userType: 'bot',
      data: {
        type: 'audio',
        value: '',
      },
    },
    {
      userType: 'user',
      data: {
        type: 'text',
        value: 'This is a message from you, it would appear like this to your users',
      },
    },
    {
      userType: 'user',
      data: {
        type: 'audio',
        value: '',
      },
    },
  ];

  const sampleVideoWidgetMessages: Array<{
    userType: 'bot' | 'user';
    data: { type: string; value: any };
  }> = [
    {
      userType: 'bot',
      data: {
        type: 'button',
        value: [
          {
            title: 'Get started',
            id: '1',
            type: 'button',
            label: 'Get started',
            order_id: 1,
          },
          {
            title: 'Go to products',
            id: '2',
            type: 'link',
            label: 'Go to products',
            order_id: 2,
            logo: 'https://cdn.1cdn.app/application/LOUISEBOT/24061813575035_Shopify.svg',
            link: 'https://louisebot.com',
          },
          {
            title: 'Have a tour',
            id: '3',
            type: 'button',
            label: 'Have a tour',
            order_id: 3,
          },
        ],
      },
    },
  ];

  const renderWidget = (bot_code: BotCode) => {
    switch (bot_code) {
      case BotTypeEnum.TEXT_SCENARIO:
      case BotTypeEnum.AI_READY_TO_USE:
      case BotTypeEnum.AI_WITH_KNOWLEDGE_BASE:
      case BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY:
      case BotTypeEnum.HUMAN_CLONE:
        return <TextWidget data={configs as IAiAppearance} messages={sampleTextWidgetMessages} />;
      case BotTypeEnum.VIDEO_SCENARIO:
        return <VideoWidget data={configs as IAiAppearance} messages={sampleVideoWidgetMessages} />;
      case BotTypeEnum.CALLBOT:
        return <CallBot data={configs as IAiAppearance} />;
      default:
        return null;
    }
  };

  const renderFloating = (appearance: IAiAppearance) => {
    switch (appearance.icon.type) {
      case 'video':
        return (
          <VideoToggleIcon
            icon={appearance.icon}
            message={appearance.isNotificationMessageActive && appearance.notificationMessage ? appearance.notificationMessage : ''}
            shape={!appearance.roundedVideo ? 'rectangular' : 'circular'}
            hasNotificationBadge={appearance.notificationBadge}
            notificationMessageColor={appearance.notificationMessageTextColor}
            hideCloseButton={appearance.hideCloseButton || false}
          />
        );
      default:
        return (
          <WidgetToggleIcon
            icon={appearance.icon}
            position={appearance.position as 'left' | 'right'}
            theme={appearance.theme as 'light' | 'dark'}
            color={appearance.color}
            message={appearance.isNotificationMessageActive && appearance.notificationMessage ? appearance.notificationMessage : ''}
            shape={!appearance.roundedVideo ? 'rectangular' : 'circular'}
            hasNotificationBadge={appearance.notificationBadge as boolean}
            notificationMessageColor={appearance.notificationMessageTextColor}
            hideCloseButton={appearance.hideCloseButton || false}
          />
        );
    }
  };

  if (!bot_code || !configs) return null;

  return (
    <div className="appearance-preview tw-w-full">
      <div
        className={`tw-flex tw-flex-col tw-gap-4 ${
          (configs as IAiAppearance).position === 'right' ? 'tw-items-end tw-justify-end' : 'tw-items-start tw-justify-start'
        }`}
      >
        {/* Widget */}
        {renderWidget(bot_code)}

        {/* Floating button */}
        {bot_code !== BotTypeEnum.HUMAN_CLONE && bot_code !== BotTypeEnum.CALLBOT ? renderFloating(configs as IAiAppearance) : null}
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(AppearancePreview);
